import React, { useState, useRef } from 'react'
import { Upload, FileText, X, Check, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'

function FileUpload({ onFileUpload, onClose }) {
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [fileContent, setFileContent] = useState('')
  const fileInputRef = useRef(null)
  
  const supportedFormats = [
    { ext: '.txt', type: 'text/plain', description: 'Plain Text Files' },
    { ext: '.md', type: 'text/markdown', description: 'Markdown Files' },
    { ext: '.rtf', type: 'text/rtf', description: 'Rich Text Format' }
  ]
  
  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }
  
  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }
  
  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileSelection(files[0])
    }
  }
  
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) {
      handleFileSelection(files[0])
    }
  }
  
  const handleFileSelection = async (file) => {
    // Validate file type
    const isSupported = supportedFormats.some(format => 
      file.name.toLowerCase().endsWith(format.ext) || file.type === format.type
    )
    
    if (!isSupported) {
      toast.error('Unsupported file format. Please upload a text file.')
      return
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024 // 5MB
    if (file.size > maxSize) {
      toast.error('File too large. Please upload a file smaller than 5MB.')
      return
    }
    
    setIsProcessing(true)
    setUploadedFile(file)
    
    try {
      const text = await readFileAsText(file)
      
      // Validate content length
      if (text.length > 50000) {
        toast.error('File content too long. Please use a file with less than 50,000 characters.')
        setIsProcessing(false)
        setUploadedFile(null)
        return
      }
      
      if (text.trim().length === 0) {
        toast.error('File appears to be empty. Please upload a file with text content.')
        setIsProcessing(false)
        setUploadedFile(null)
        return
      }
      
      setFileContent(text)
      toast.success('File uploaded successfully!')
    } catch (error) {
      toast.error('Failed to read file. Please try again.')
      console.error('File reading error:', error)
      setUploadedFile(null)
    } finally {
      setIsProcessing(false)
    }
  }
  
  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = (e) => {
        try {
          let text = e.target?.result
          
          // Handle different file encodings
          if (typeof text !== 'string') {
            text = new TextDecoder('utf-8').decode(text)
          }
          
          // Clean up text
          text = text.replace(/\r\n/g, '\n') // Normalize line endings
          text = text.replace(/\r/g, '\n')   // Handle old Mac line endings
          
          resolve(text)
        } catch (error) {
          reject(error)
        }
      }
      
      reader.onerror = () => reject(new Error('Failed to read file'))
      
      // Read as text with UTF-8 encoding
      reader.readAsText(file, 'UTF-8')
    })
  }
  
  const handleUseFile = () => {
    if (fileContent.trim()) {
      onFileUpload(fileContent.trim())
    }
  }
  
  const handleClearFile = () => {
    setUploadedFile(null)
    setFileContent('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            Upload Text File
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {!uploadedFile ? (
            <>
              {/* Upload Area */}
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <div className="p-4 bg-gray-100 rounded-full">
                      <Upload className="w-8 h-8 text-gray-600" />
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {isDragging ? 'Drop your file here' : 'Upload a text file'}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Drag and drop a file here, or click to browse
                    </p>
                    
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="btn-primary"
                      disabled={isProcessing}
                    >
                      {isProcessing ? 'Processing...' : 'Choose File'}
                    </button>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".txt,.md,.rtf,text/plain,text/markdown,text/rtf"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
              
              {/* Supported Formats */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Supported File Formats</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {supportedFormats.map((format, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <div>
                        <div className="font-medium text-gray-900">{format.ext}</div>
                        <div className="text-gray-600">{format.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Limitations */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  File Requirements
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Maximum file size: 5MB</li>
                  <li>• Maximum content length: 50,000 characters</li>
                  <li>• File must contain readable text content</li>
                  <li>• UTF-8 encoding recommended</li>
                </ul>
              </div>
            </>
          ) : (
            <>
              {/* File Info */}
              <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex-shrink-0">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-green-900">{uploadedFile.name}</h3>
                  <p className="text-sm text-green-700">
                    {formatFileSize(uploadedFile.size)} • {fileContent.length.toLocaleString()} characters
                  </p>
                </div>
                <button
                  onClick={handleClearFile}
                  className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {/* Content Preview */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Content Preview</h4>
                <div className="max-h-64 overflow-y-auto p-4 bg-gray-50 rounded-lg border">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                    {fileContent.length > 1000 
                      ? `${fileContent.substring(0, 1000)}...\n\n[Content truncated - ${fileContent.length.toLocaleString()} total characters]`
                      : fileContent
                    }
                  </pre>
                </div>
              </div>
              
              {/* Content Stats */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">
                    {fileContent.length.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Characters</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">
                    {fileContent.trim().split(/\s+/).length.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Words</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">
                    {fileContent.split('\n').length.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Lines</div>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          
          {uploadedFile && (
            <>
              <button
                onClick={handleClearFile}
                className="btn-secondary"
              >
                Choose Different File
              </button>
              
              <button
                onClick={handleUseFile}
                className="btn-primary flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Use This File
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default FileUpload
